import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { PageHeader, EmptyState, Button } from "../components/UI";
import { Sparkles, Plus, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

const examples = [
  "Poster jobs me minimum 200 sheet wastage add karo.",
  "Komori GL437 jobs me plate cost ₹1200/set use karo.",
  "School notebook jobs me binding cost ₹2 per copy add karo.",
  "Hospital files me minimum margin 15% rakho.",
  "Large format jobs me delivery charge double karo.",
];

export default function AIInstructions() {
  const [list, setList] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setList((await api.get("/ai-instructions")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (preset) => {
    const val = (preset || text).trim();
    if (!val) return;
    try {
      await api.post("/ai-instructions", { text: val });
      setText("");
      toast.success("Rule added");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const toggle = async (i) => {
    try {
      await api.patch(`/ai-instructions/${i.id}`, { text: i.text, is_active: !i.is_active });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (i) => {
    if (!window.confirm("Delete this rule?")) return;
    try { await api.delete(`/ai-instructions/${i.id}`); load(); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-4 lg:p-8 max-w-4xl" data-testid="ai-instructions-page">
      <PageHeader
        title="AI Pricing Rules"
        subtitle="Write your costing rules in plain Hinglish · AI applies them automatically to every quote"
      />

      <div className="card-flat p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={18} className="text-[#C1121F]"/>
          <h3 className="font-display font-bold text-lg">Add a new rule</h3>
        </div>
        <div className="flex gap-2">
          <input
            data-testid="rule-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="input-base flex-1"
            placeholder="e.g. Poster jobs me 200 sheet wastage add karo at ₹1.50 per sheet"
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
          <Button onClick={() => add()} data-testid="add-rule-btn"><Plus size={14}/> Add</Button>
        </div>
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#94A3B8] mb-2">Example rules · click to add</div>
          <div className="flex flex-wrap gap-1.5">
            {examples.map((ex, i) => (
              <button key={i} onClick={() => add(ex)} className="text-[11px] px-2 py-1 bg-[#F8F9FA] border border-[#E5E7EB] rounded text-[#475569] hover:border-[#C1121F] hover:text-[#C1121F]">
                {ex}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? <div className="text-[#475569]">Loading…</div> :
       list.length === 0 ? (
        <EmptyState icon={Sparkles} title="No rules yet"
          hint="Add your first costing rule above. AI will apply it to every Pricing Calculator quote." />
      ) : (
        <div className="space-y-2" data-testid="rules-list">
          {list.map((i) => (
            <div key={i.id} className={`card-flat p-4 flex items-center gap-3 ${!i.is_active ? "opacity-50" : ""}`} data-testid={`rule-${i.id}`}>
              <button onClick={() => toggle(i)} className="flex-shrink-0">
                {i.is_active ? <ToggleRight size={28} className="text-[#C1121F]"/> : <ToggleLeft size={28} className="text-[#94A3B8]"/>}
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm">{i.text}</div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">
                  by {i.created_by_name || "admin"} · {i.created_at.slice(0, 10)}
                </div>
              </div>
              <button onClick={() => remove(i)} className="text-[#94A3B8] hover:text-red-600 p-1.5"><Trash2 size={14}/></button>
            </div>
          ))}
          <div className="text-xs text-[#94A3B8] mt-3 px-1">
            💡 Tip: keep rules specific (mention product type, amount, and condition). AI is more accurate with precise inputs.
          </div>
        </div>
      )}
    </div>
  );
}
